import { AppDialog } from '../Common/AppDialog'
import type { FeatureKey, TierId } from '../../services/subscriptionGuard'
import { LIFETIME_PASSES } from '../../config/lifetimeEntitlements'
import {
  WORKSPACE_TIER_CAP_KEYS,
  type WorkspaceTierCapKey,
  resolveTeamWorkspaceCaps,
} from '../../config/planLimitsCore'
import { TIER_IDS, normalizeTierId } from '../../config/PricingConfig'
import { STRINGS_NLS } from '../../nls/strings_nls'

const FEATURE_LABELS: Record<FeatureKey, string> = {
  workspaces: 'workspace',
  storageGB: 'storage',
  chatHistoryDays: 'chat history retention',
  chatMessageLimit: 'chat messages',
  activeIntegrations: 'active integrations',
  meetingsPerMonth: 'meetings per month',
  webhookCallsPerMonth: 'monthly webhook calls',
  auditLogRetentionDays: 'audit-log retention',
  syncSpeedSeconds: 'sync speed',
  aiChatHistories: 'AI chat histories',
  aiContextAttachments: 'AI context attachments',
  maxSavedCharts: 'saved charts',
  whatsappMonthlyCap: 'WhatsApp monthly messages',
}

export type PaywallKey = FeatureKey | WorkspaceTierCapKey

type UpgradeModalProps = {
  open: boolean
  featureKey: PaywallKey
  plan: TierId | string
  currentUsage: number
  limit: number
  onDismiss: () => void
  onUpgrade: () => void
}

export const UpgradeModal = ({ open, featureKey, plan, currentUsage, limit, onDismiss, onUpgrade }: UpgradeModalProps) => {
  const isCapKey = WORKSPACE_TIER_CAP_KEYS.includes(featureKey as WorkspaceTierCapKey)
  const nlsCapKey = `NLSWorkspaceCap_${featureKey}` as keyof typeof STRINGS_NLS
  const label = isCapKey
    ? (STRINGS_NLS[nlsCapKey]?.text ?? featureKey)
    : (FEATURE_LABELS[featureKey as FeatureKey] ?? featureKey)

  const normPlan = normalizeTierId(plan)
  const tierIndex = TIER_IDS.indexOf(normPlan)
  const nextTier = tierIndex >= 0 && tierIndex < TIER_IDS.length - 1 ? TIER_IDS[tierIndex + 1] : null

  let nextTierMessage = ''
  if (nextTier && isCapKey) {
    const nextCap = resolveTeamWorkspaceCaps(nextTier)[featureKey as WorkspaceTierCapKey]
    const nextLimitStr = nextCap === -1 ? 'Unlimited' : nextCap.toLocaleString('en-IN')
    const nextTierName = nextTier.replace('_TIER', '').toLowerCase()
    nextTierMessage = ` Next tier (${nextTierName}) allows ${nextLimitStr}.`
  }

  return (
    <AppDialog
      open={open}
      title={featureKey === 'workspaces' ? 'Workspace ownership limit reached' : 'Plan limit reached'}
      message={
        featureKey === 'workspaces'
          ? <p>Each account can own one Personal Space and one Team workspace. Invites and public-team requests are still available for teams owned by others.</p>
          : <><p>
            Your {normPlan.replaceAll('_', ' ').toLowerCase()} plan allows {limit === -1 ? 'Unlimited' : limit} {label}.
            {' '}Current usage is {currentUsage}.{nextTierMessage} Upgrade the workspace plan to continue.
          </p><div className="upgradeLifetimeOptions">{Object.values(LIFETIME_PASSES).map((pass) => <span key={pass.name}>{pass.name} · ₹{pass.priceInr}</span>)}</div></>
      }
      onDismiss={onDismiss}
      actions={featureKey === 'workspaces'
        ? [{ label: 'Got it', variant: 'primary', onClick: onDismiss, autoFocus: true }]
        : [
            { label: 'Not now', onClick: onDismiss },
            { label: 'View plans', variant: 'primary', onClick: onUpgrade, autoFocus: true },
          ]}
    />
  )
}
